import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, Timestamp, updateDoc, increment } from 'firebase/firestore';
import './Blog.css';
import { Helmet } from 'react-helmet-async';
import PageTitle from '../../components/PageTitle';

import { GiRoundStar } from "react-icons/gi";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  isDraft?: boolean;
  likes?: number;
  tags?: string[];
}

const BlogDetail = ({ isAdmin }: { isAdmin: boolean }) => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [likedPosts, setLikedPosts] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;
      const docRef = doc(db, 'blog', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPost({
          id: docSnap.id,
          title: data.title || '',
          content: data.content || '',
          createdAt: data.createdAt || Timestamp.now(),
          updatedAt: data.updatedAt,
          tags: (data.tags as string[] | undefined) || [],
          isDraft: data.isDraft || false,
          likes: data.likes || 0,
        } as BlogPost);
      } else {
        navigate('/blog');
      }
    };
    const savedLikes = JSON.parse(localStorage.getItem('blog_liked_posts') || '[]');
    setLikedPosts(savedLikes);
    fetchPost();
  }, [id, navigate]);

  const handleLike = async () => {
    if (!post) return;
    const isLiked = likedPosts.includes(post.id);
    const postRef = doc(db, 'blog', post.id);

    try {
      await updateDoc(postRef, {
        likes: increment(isLiked ? -1 : 1)
      });

      const newLikedPosts = isLiked
        ? likedPosts.filter(id => id !== post.id)
        : [...likedPosts, post.id];

      setLikedPosts(newLikedPosts);
      localStorage.setItem('blog_liked_posts', JSON.stringify(newLikedPosts));

      setPost(prev => prev ? { ...prev, likes: Math.max(0, (prev.likes || 0) + (isLiked ? -1 : 1)) } : null);
    } catch (error) {
      console.error("Error liking post:", error);
    }
  };

  const [backHovered, setbackHovered] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [LikeHovered, setLikeHovered] = useState(false);

  // ★ 共有ボタンの処理を関数として切り出し
  const handleShare = () => {
    if (!post) return;

    function fallbackCopyText(text: string) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert("URLをコピーしました！");
      } catch (err) {
        alert("URLのコピーに失敗しました。手動でコピーしてください:\n" + text);
      }
      document.body.removeChild(textArea);
    }

    if (navigator.share) {
      navigator.share({
        title: post.title || "無題",
        text: post.content.replace(/<[^>]*>/g, '').substring(0, 100),
        url: window.location.href,
      });
    } else {
      const url = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url)
          .then(() => alert("URLをコピーしました！"))
          .catch(() => fallbackCopyText(url));
      } else {
        fallbackCopyText(url);
      }
    }
  };

  if (!post) {
    return (
      <div className="blog-container" style={{ paddingTop: '100px', color: 'white', textAlign: 'center' }}>
        <div className="blog-page-wrapper"></div>
        読み込み中...
      </div>
    );
  }

  return (
    <div className="blog-container" style={{ paddingTop: '100px', color: 'white', maxWidth: '800px', margin: '0 auto', paddingInline: '20px' }}>
      <Helmet>
        <title>{post.title} | 毒と花と6畳間</title>
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.content.replace(/<[^>]*>/g, '').substring(0, 100)} />
        <meta property="og:type" content="article" />
       {(() => {
        const match = post.content.match(/<img[^>]+src="([^">]+)"/);
        return match ? <meta property="og:image" content={match[1]} /> : null;
      })()}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="blog-page-wrapper"></div>
      <PageTitle title={post.title} />

      <article className="post-item" style={{ marginTop: '20px', textAlign: 'left' }}>
          <h1 style={{ fontFamily: "Zen Maru Gothic", fontSize: '2.4rem', marginBottom: '10px', borderBottom: '2px solid #ffffff' }}>
            {post.isDraft && <span className="draft-badge">下書き</span>}
          <GiRoundStar /> {post.title}
        </h1>

        {/* タグ表示 */}
        {post.tags && post.tags.length > 0 && (
          <div className="post-tags">
            {post.tags.map((tag, index) => (
              <span
                key={index}
                className="post-tag-chip"
                onClick={() => navigate(`/blog?tag=${encodeURIComponent(tag)}`)}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div
          className="post-content"
          style={{ marginTop: '30px' }}
          dangerouslySetInnerHTML={{ __html: post.content.replace(/<hr\s*\/?>/gi, '') }}
        />

        {/* ★ 共有ボタン・いいねボタン・日時 をまとめたフッター */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', marginTop: '40px' }}>
          {/* 記事一覧へ */}
          <div>
            <button
              onClick={() => navigate('/blog')}
              onMouseEnter={() => setbackHovered(true)}
              onMouseLeave={() => setbackHovered(false)}
              style={{
                alignItems: 'left',
                background: '#135a5f38',
                border: '1px solid #6a9faab4',
                cursor: 'pointer',
                color: '#00d4ff',
                fontSize: '0.8rem',
                padding: window.innerWidth <= 1024 ? '12px 16px' : '6px 12px',
                borderRadius: window.innerWidth <= 1024 ? '40px' : '20px',
                opacity: backHovered ? 0.6 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              ＜＜ 記事一覧
            </button>
          </div>

          {/* 共有・いいね・日時 を右側にまとめる */}
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <button
              onClick={handleShare}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              style={{
                background: '#4e999e38',
                border: '1px solid #6a9faab4',
                cursor: 'pointer',
                color: '#00d4ff',
                fontSize: '0.8rem',
                padding: window.innerWidth <= 1024 ? '12px 16px' : '6px 12px',
                borderRadius: window.innerWidth <= 1024 ? '40px' : '20px',
                opacity: isHovered ? 0.6 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <span style={{ marginRight: '4px' }}>🔗</span> 共有
            </button>

            <button
              className="like-button"
              onClick={handleLike}
              onMouseEnter={() => setLikeHovered(true)}
              onMouseLeave={() => setLikeHovered(false)}
              style={{
                background: likedPosts.includes(post.id) ? '#ff4d4f' : '#ff4d4f22',
                border: '1px solid #ff4d4f',
                color: likedPosts.includes(post.id) ? '#fff' : '#ff4d4f',
                padding: window.innerWidth <= 1024 ? '8px 16px' : '4px 12px',
                borderRadius: '20px',
                cursor: 'pointer',
                fontFamily: "Zen Maru Gothic",
                fontSize: window.innerWidth <= 1024 ? '1rem' : '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                opacity: LikeHovered ? 0.6 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              ❤️ {post.likes || 0}
            </button>

            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', margin: 0, display: 'inline-flex', alignItems: 'center' }}>
              {post.updatedAt && !post.updatedAt.isEqual(post.createdAt) ? (
                <>
                  最終更新 : {post.updatedAt.toDate().toLocaleString('ja-JP', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                  <br />
                  初版投稿 : {post.createdAt ? post.createdAt.toDate().toLocaleString('ja-JP', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : ''}
                </>
              ) : (
                `投稿日時 : ${post.createdAt ? post.createdAt.toDate().toLocaleString('ja-JP', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : ''}`
              )}
            </small>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;